import { isPaymentAttachmentField } from '../../../helpers/paymentAttachmentCodes'
import { effectiveLocationWithAllowedLocation } from '../../../helpers/utils'
import { REFUND_APPROVAL_TEXT } from '../../constants/refundApprovalText'

const EMPTY_REFUND_DETAIL_VALUES = new Set(['', '-', '—', 'null', 'nil', 'undefined'])

export const hasRefundDetailValue = (value) => {
  if (value == null) return false
  if (Array.isArray(value)) return value.length > 0

  const normalized = String(value).trim().toLowerCase()
  return !EMPTY_REFUND_DETAIL_VALUES.has(normalized)
}

const filterVisibleRefundFields = (fields) =>
  fields.filter((field) => hasRefundDetailValue(field?.label) && hasRefundDetailValue(field?.value))

export const getFormattedRefundDate = (date) => {
  if (!hasRefundDetailValue(date)) return null

  const parsedDate = new Date(date)
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate.toLocaleString()
}

export const getRefundWeight = (weight = 0) => {
  return Number(weight).toFixed(2) === '0.00' ? 'N/A' : Number(weight).toFixed(2)
}

export const getRefundRowSubtotal = (extendedPrice = 0, discountedPrice = 0) => {
  return discountedPrice ? extendedPrice - discountedPrice : extendedPrice
}

export const buildRefundSummaryFields = (activity) => {
  if (!activity) return []

  const processingDate = activity.approved_at || activity.rejected_at

  const fields = [
    { label: 'Fecha de documento', value: getFormattedRefundDate(activity.created_at) },
    { label: 'Número de devolución', value: activity.refund_db_ref },
    {
      label: 'Fecha de ejecución',
      value: getFormattedRefundDate(activity.created_date || activity.collected_at)
    },
    { label: 'Creado por', value: activity.salesman_name },
    { label: 'Número de factura', value: activity.invoice_number },
    { label: 'Comentarios generales', value: activity.comments },
    { label: REFUND_APPROVAL_TEXT.refundMotiveLabel, value: activity.motive },
    {
      label: 'Dentro de geocerca',
      value:
        effectiveLocationWithAllowedLocation(
          activity.effective_location,
          activity.has_allowed_location
        )
    },
    { label: 'Bodega', value: activity.warehouse_name },
    {
      label: 'Contabilizado por',
      value: activity.processed_by_user_db_ref || activity.processed_by_name
    },
    {
      label: 'Fecha de procesamiento',
      value: getFormattedRefundDate(processingDate)
    },
    { label: 'NCR', value: activity.ncr_number }
  ]

  if (activity.global_motive || activity.rejected_at) {
    fields.push({
      label: 'Motivo de rechazo',
      value: activity.global_motive,
      fullWidth: true
    })
  }

  return filterVisibleRefundFields(fields)
}

export const buildRefundFields = (activity, customFields = []) => {
  const summaryLabels = new Set(
    buildRefundSummaryFields(activity).map(({ label }) => label?.trim()?.toLowerCase())
  )

  if (!customFields?.length) return []

  return customFields
    .filter((field) => field && !isPaymentAttachmentField(field))
    .map(({ label, value }) => ({
      label,
      value
    }))
    .filter(({ label, value }) => hasRefundDetailValue(label) && hasRefundDetailValue(value))
    .filter(({ label }) => !summaryLabels.has(label?.trim()?.toLowerCase()))
}

export const parseRefundProductDetails = (details = []) => {
  const extraColumns = new Set()

  const processedDetails = (Array.isArray(details) ? details : []).map((product) => {
    const customFieldValues = {}

    try {
      let customFields = product.custom_fields_item

      if (typeof customFields === 'string' && customFields.trim() !== '') {
        customFields = JSON.parse(customFields)
      }

      if (Array.isArray(customFields)) {
        customFields.forEach(({ label, value }) => {
          extraColumns.add(label)
          customFieldValues[label] = value ?? 'N/A'
        })
      }
    } catch (error) {
      console.error('Error parsing custom_fields_item:', error)
    }

    return { ...product, customFieldValues }
  })

  return {
    processedDetails,
    extraColumns: [...extraColumns]
  }
}
